import { getValidAccessToken, refreshAccessToken } from '../auth/token';

import { ApiError, ClientErrorCode, apiErrorFromProblem, type ProblemDetails } from './errors';
import { buildUrl } from './http';
import { resumableUpload, type UploadStatus } from './resumableUpload';
import type { FileRef } from './types';

/** Server-side limit for a single upload (ARCHITECTURE §4.7: ≤ 200 MB). */
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

/**
 * Files above this size are sent in chunks with retries and resume (`POST /api/files/uploads`, docs/modules/files.md
 * "Resumable uploads"): a single request that long breaks on a slow link (a proxy reads a request for a minute at most).
 * Smaller files, and uploads to another endpoint (`path`), go in one request.
 */
export const RESUMABLE_THRESHOLD_BYTES = 4 * 1024 * 1024;

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–100 */
  percent: number;
}

export interface UploadOptions {
  /** Defaults to the Files module endpoint `/api/files`. */
  path?: string;
  /** Multipart field name (default `file`). */
  fieldName?: string;
  /** Extra multipart fields. */
  fields?: Record<string, string>;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  /** Large files only: retries, waiting for the network and resumed uploads, for the progress row to show. */
  onStatus?: (status: UploadStatus) => void;
  /** Large files only: what the calling module needs to continue the upload after a page reload (pendingUploads.ts). */
  resume?: { meta?: Record<string, unknown> };
}

interface XhrResult {
  status: number;
  body: unknown;
}

function sendXhr(
  url: string,
  form: FormData,
  token: string | null,
  options: UploadOptions,
): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.responseType = 'json';
    xhr.setRequestHeader('Accept', 'application/json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return;
      options.onProgress?.({
        loaded: event.loaded,
        total: event.total,
        percent: Math.min(100, Math.round((event.loaded / event.total) * 100)),
      });
    });
    xhr.addEventListener('load', () => resolve({ status: xhr.status, body: xhr.response as unknown }));
    xhr.addEventListener('error', () =>
      reject(new ApiError({ status: 0, code: ClientErrorCode.network, message: 'Network error' })),
    );
    xhr.addEventListener('abort', () =>
      reject(new ApiError({ status: 0, code: ClientErrorCode.aborted, message: 'Upload cancelled' })),
    );

    if (options.signal) {
      if (options.signal.aborted) {
        reject(new ApiError({ status: 0, code: ClientErrorCode.aborted, message: 'Upload cancelled' }));
        return;
      }
      options.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

export type { UploadStatus } from './resumableUpload';

/**
 * Uploads a file with progress reporting (XHR, since fetch has no upload progress).
 * Large files to the Files module go in chunks that are retried and survive a page reload (see
 * {@link RESUMABLE_THRESHOLD_BYTES}); the result is the same either way.
 * Resolves with the stored `FileRef`; rejects with `ApiError` (code `client.aborted` when cancelled).
 */
export async function uploadFile(file: File, options: UploadOptions = {}): Promise<FileRef> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ApiError({ status: 413, code: 'files.too_large', message: 'File is too large' });
  }
  if (options.path === undefined && options.fields === undefined && file.size > RESUMABLE_THRESHOLD_BYTES) {
    return resumableUpload(file, options);
  }
  const form = new FormData();
  form.append(options.fieldName ?? 'file', file, file.name);
  for (const [key, value] of Object.entries(options.fields ?? {})) form.append(key, value);

  const url = buildUrl(options.path ?? '/files');
  let result = await sendXhr(url, form, await getValidAccessToken(), options);
  if (result.status === 401) {
    const token = await refreshAccessToken();
    if (token) result = await sendXhr(url, form, token, options);
  }
  if (result.status < 200 || result.status >= 300) {
    const problem =
      typeof result.body === 'object' && result.body !== null ? (result.body as ProblemDetails) : undefined;
    throw apiErrorFromProblem(result.status, problem);
  }
  return result.body as FileRef;
}
